/** You › Account: profile email, invites, sign out. */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { LogOut, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { errorText, useToast } from '../../components/ui.js'
import type { CreatedInvite, Invite } from '../../lib/api.js'
import { useBackend } from '../../lib/backend.js'
import { useSession } from '../../lib/queries.js'
import { EmailCompletion } from '../auth.js'
import { Section, ShowMore } from './shared.js'

/** Sections here: add account-level content below Profile. */
export function AccountSection() {
  return (
    <>
      <Profile />
      <Invites />
      <SignOut />
    </>
  )
}

function Profile() {
  const session = useSession()
  return (
    <Section id="profile" title="Profile">
      <div className="mx-4 grid gap-2">
        {session.data?.email ? (
          <>
            <span className="text-[15px]">Email username: {session.data.email}</span>
            <p className="m-0 text-[14px] text-text-3">
              Mailbox ownership is not verified. Viko does not send email.
            </p>
          </>
        ) : (
          <EmailCompletion />
        )}
      </div>
    </Section>
  )
}

/** Settings → Invites: invite someone (a few per account), copy the link once, revoke unused ones. */
function Invites() {
  const b = useBackend()
  const toast = useToast()
  const [list, setList] = useState<Invite[] | null>(null)
  const [note, setNote] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [created, setCreated] = useState<CreatedInvite | null>(null)
  const [busy, setBusy] = useState(false)
  const load = async () => {
    try {
      setList(await b.api.invites())
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once
  useEffect(() => {
    void load()
  }, [])
  const create = async () => {
    setBusy(true)
    try {
      const inv = await b.api.createInvite(recipientEmail.trim(), note.trim() || undefined)
      setCreated(inv)
      setNote('')
      setRecipientEmail('')
      await load()
    } catch (e) {
      toast(
        e instanceof Error && 'code' in e && (e as { code: string }).code === 'quota_invites'
          ? 'You’ve used all your invites. Revoke an unused one to make another.'
          : errorText(e),
        'error',
      )
    } finally {
      setBusy(false)
    }
  }
  const statusText: Record<Invite['status'], string> = {
    active: 'Not used yet',
    used: 'Used',
    expired: 'Expired',
    revoked: 'Revoked',
    unknown: 'Unknown',
  }
  return (
    <Section id="invites" title="Invites">
      <form
        className="mx-4 mb-2 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          void create()
        }}
      >
        <label className="field min-w-[200px] flex-1">
          <span>Recipient email</span>
          <input
            className="input"
            type="email"
            required
            maxLength={254}
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="sam@example.com"
          />
        </label>
        <label className="field min-w-[200px] flex-1">
          <span>Note (optional)</span>
          <input
            className="input"
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Sam"
          />
        </label>
        <Button type="submit" className="!min-h-11" icon={<Plus size={15} />} disabled={busy}>
          Invite someone
        </Button>
      </form>
      {created ? (
        <div className="card mx-4 mb-2 grid gap-2 p-3" role="status">
          <span className="text-[14px] text-text-2">
            Share this link with {created.recipientEmail} — it works once and expires{' '}
            {new Date(created.expiresAt).toLocaleDateString()}. Viko won’t show it again.
          </span>
          <div className="flex items-center gap-2 rounded-lg bg-code-bg p-2">
            <code className="flex-1 font-mono text-[14px] break-all">{created.url}</code>
            <Button
              size="sm"
              className="!min-h-11"
              onClick={() => {
                void navigator.clipboard?.writeText(created.url)
                toast('Invite link copied')
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      ) : null}
      <ShowMore items={list ?? []} limit={3} label="invites">
        {(shown) => (
          <ul className="m-0 list-none p-0" aria-label="Invites">
            {shown.map((inv) => (
              <li key={inv.id} className="row !cursor-default items-center">
                <span className="grid flex-1 gap-0.5">
                  <span className="text-[15px] font-medium">
                    {inv.recipientEmail ?? 'Legacy invite'}
                  </span>
                  <span className="row__meta">
                    {inv.note ? `${inv.note} · ` : ''}
                    {statusText[inv.status]} · created{' '}
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </span>
                </span>
                {inv.status === 'active' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="!min-h-11"
                    onClick={async () => {
                      try {
                        await b.api.revokeInvite(inv.id)
                        await load()
                      } catch (e) {
                        toast(errorText(e), 'error')
                      }
                    }}
                  >
                    Revoke
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </ShowMore>
      {list && list.length === 0 ? (
        <p className="mx-4 m-0 text-[14px] text-text-3">
          Viko is invite-only. Each account can invite a few people.
        </p>
      ) : null}
    </Section>
  )
}

function SignOut() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  return (
    <div className="px-4 pt-8 pb-10">
      <Button
        variant="ghost"
        icon={<LogOut size={15} />}
        onClick={async () => {
          await b.api.logout().catch(() => {})
          b.hub.disconnect()
          qc.clear()
          void nav({ to: '/signin' })
        }}
      >
        Sign out
      </Button>
    </div>
  )
}
